import type { StringKey } from '@ipfabric/utils';
import { FormField } from 'components/FormField/FormField';
import {
  MultiSelect,
  type MultiSelectProps,
} from 'components/Select/MultiSelect';
import {
  type GetSelectOptionValueFunction,
  getSelectOptionValueDefault,
} from 'components/Select/Select';
import type { FormikProps } from 'formik';
import type { JSX, ReactNode } from 'react';
import { fieldHasError } from 'utils/formik/fieldHasError';
import type { NestedFormikName } from 'utils/formik/nestedFormikName';

type Props<TFormikValues, TValue> = Omit<
  MultiSelectProps<TValue>,
  'value' | 'onChange'
> & {
  label?: ReactNode;
  formik: FormikProps<TFormikValues>;
  name: StringKey<TFormikValues> | NestedFormikName;
  onChange?: (value: TValue[]) => void;
  noBottomSpacing?: boolean;
  dataTestId?: string;
};

export const FormikMultiSelectField = <TFormikValues, TValue>({
  label,
  formik,
  name,
  onChange,
  noBottomSpacing,
  // Need to assign default comparable option value getter since TS is not able
  // to decide if it can be optional from generic TValue for some reason
  getOptionValue = getSelectOptionValueDefault as GetSelectOptionValueFunction<TValue>,
  ...props
}: Props<TFormikValues, TValue>): JSX.Element => {
  const field = formik.getFieldProps<TValue[]>(name);
  const hasError = fieldHasError<TFormikValues>(formik, name);

  const handleChange = (value: TValue[]) => {
    field.onChange({
      target: {
        name: field.name,
        value,
      },
    });
    onChange?.(value);
  };

  const handleBlur = () => {
    field.onBlur({
      target: {
        name: field.name,
      },
    });
  };

  return (
    <FormField
      label={label}
      error={hasError ? (formik.errors[name] as string) : undefined}
      noBottomSpacing={noBottomSpacing}
    >
      <MultiSelect
        {...props}
        hasError={hasError}
        onChange={handleChange}
        onBlur={handleBlur}
        value={field.value}
        getOptionValue={getOptionValue}
      />
    </FormField>
  );
};
